class HeaderLocalizationSwitcher extends HTMLElement {
  constructor() {
    super()

    this.loaded = false
    this.loading = false
    this.button = this.querySelector('button')
    this.dialog = null
    this.initialCountry = null
    this.initialLanguage = null
    this.languageMap = {}
    this.countryDropdownOpen = false

    this.handleOutsidePointer = this.handleOutsidePointer.bind(this)

    this.button.addEventListener('click', (event) => {
      event.stopPropagation()
      this.open()
    })
  }

  async open() {
    if (!this.loaded) {
      const loaded = await this.load()
      if (!loaded) return
    }

    if (this.dialog && !this.dialog.open) {
      this.closeCountryDropdown()
      this.resetSearch()
      this.filterLanguages()
      this.updateApplyState()
      this.dialog.showModal()
      this.button.setAttribute('aria-expanded', 'true')
      this.trackPlausible('Localization Dialog Open')
    }
  }

  close() {
    this.closeCountryDropdown()
    if (this.dialog?.open) {
      this.dialog.close()
    }
    this.button.setAttribute('aria-expanded', 'false')
  }

  trackPlausible(eventName) {
    if (typeof window.plausible === 'function') {
      window.plausible(eventName)
    }
  }

  async load() {
    if (this.loading || this.loaded) return this.loaded

    this.loading = true

    try {
      const url = new URL(window.location.href)
      url.searchParams.set('section_id', 'localization-switcher')
      const response = await fetch(url.toString())

      if (!response.ok) throw new Error('Failed to load localization switcher')

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const dialog = doc.querySelector('#HeaderLocalizationDialog')

      if (!dialog) throw new Error('Localization switcher dialog missing')

      this.appendChild(dialog)
      this.dialog = dialog
      this.cacheElements()
      this.fixFormReturnTo()
      this.bindDialog()
      this.loaded = true
      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      this.loading = false
    }
  }

  cacheElements() {
    this.form = this.dialog.querySelector('form')
    this.countryCombobox = this.dialog.querySelector('#HeaderLocalizationCountryCombobox')
    this.countryTrigger = this.dialog.querySelector('#HeaderLocalizationCountryTrigger')
    this.countryTriggerFlag = this.dialog.querySelector('#HeaderLocalizationCountryTriggerFlag')
    this.countryTriggerLabel = this.dialog.querySelector('#HeaderLocalizationCountryTriggerLabel')
    this.countryTriggerCurrency = this.dialog.querySelector('#HeaderLocalizationCountryTriggerCurrency')
    this.countryDropdown = this.dialog.querySelector('#HeaderLocalizationCountryDropdown')
    this.searchInput = this.dialog.querySelector('#HeaderLocalizationCountrySearch')
    this.countryList = this.dialog.querySelector('#HeaderLocalizationCountryList')
    this.countryInput = this.dialog.querySelector('#HeaderLocalizationCountry')
    this.languageField = this.dialog.querySelector('#HeaderLocalizationLanguageField')
    this.languageSelect = this.dialog.querySelector('#HeaderLocalizationLanguage')
    this.applyButton = this.dialog.querySelector('#HeaderLocalizationApply')
    this.emptyState = this.dialog.querySelector('#HeaderLocalizationCountryEmpty')
    this.countLabel = this.dialog.querySelector('#HeaderLocalizationCountryCount')
    this.countryOptions = [...this.dialog.querySelectorAll('.localization-country-option')]
    this.closeButton = this.dialog.querySelector('.close-button')

    const mapEl = this.dialog.querySelector('#HeaderLocalizationLanguageMap')
    try {
      this.languageMap = mapEl ? JSON.parse(mapEl.textContent) : {}
    } catch (error) {
      console.error('Failed to parse localization language map', error)
      this.languageMap = {}
    }

    this.initialCountry = this.countryInput?.value || ''
    this.initialLanguage = this.languageSelect?.value || this.form?.querySelector('input[name="language_code"]')?.value || ''
    this.allLanguageOptions = this.languageSelect
      ? [...this.languageSelect.options].map((option) => ({
          value: option.value,
          label: option.textContent.trim(),
        }))
      : []
  }

  bindDialog() {
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target === this.closeButton || this.closeButton?.contains(event.target)) {
        this.close()
      }
    })

    this.dialog.addEventListener('close', () => {
      this.closeCountryDropdown()
      this.button.setAttribute('aria-expanded', 'false')
    })

    this.countryTrigger?.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.toggleCountryDropdown()
    })

    this.searchInput?.addEventListener('input', () => {
      this.filterCountries()
    })

    this.countryOptions.forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectCountry(option)
      })
    })

    this.languageSelect?.addEventListener('change', () => {
      this.updateApplyState()
    })

    this.form?.addEventListener('submit', (event) => {
      if (this.applyButton?.disabled) {
        event.preventDefault()
        return
      }
      this.trackPlausible('Localization Update')
      this.fixFormReturnTo()
    })
  }

  fixFormReturnTo() {
    if (!this.form) return

    const returnToInput = this.form.querySelector('input[name="return_to"]')
    if (!returnToInput) return

    const url = new URL(window.location.href)
    url.searchParams.delete('section_id')
    returnToInput.value = `${url.pathname}${url.search}${url.hash}`
  }

  handleOutsidePointer(event) {
    if (!this.countryDropdownOpen) return
    if (this.countryCombobox?.contains(event.target)) return
    this.closeCountryDropdown()
  }

  toggleCountryDropdown() {
    if (this.countryDropdownOpen) {
      this.closeCountryDropdown()
    } else {
      this.openCountryDropdown()
    }
  }

  openCountryDropdown() {
    if (!this.countryDropdown || !this.countryTrigger) return

    this.countryDropdown.hidden = false
    this.countryTrigger.setAttribute('aria-expanded', 'true')
    this.countryDropdownOpen = true
    this.resetSearch()
    this.scrollSelectedIntoView()
    document.addEventListener('pointerdown', this.handleOutsidePointer, true)
    requestAnimationFrame(() => this.searchInput?.focus())
  }

  closeCountryDropdown() {
    if (!this.countryDropdown || !this.countryTrigger) return

    this.countryDropdown.hidden = true
    this.countryTrigger.setAttribute('aria-expanded', 'false')
    this.countryDropdownOpen = false
    this.resetSearch()
    document.removeEventListener('pointerdown', this.handleOutsidePointer, true)
  }

  selectCountry(option) {
    this.countryOptions.forEach((item) => {
      const selected = item === option
      item.setAttribute('aria-selected', selected ? 'true' : 'false')
      item.classList.toggle('bg-gray-100', selected)
    })

    if (this.countryInput) {
      this.countryInput.value = option.dataset.value
    }

    this.updateCountryTrigger(option)
    this.closeCountryDropdown()
    this.filterLanguages(option)
    this.updateApplyState()
  }

  updateCountryTrigger(option) {
    if (this.countryTriggerLabel) {
      this.countryTriggerLabel.textContent = option.dataset.title || ''
    }

    if (this.countryTriggerCurrency) {
      this.countryTriggerCurrency.textContent = option.dataset.currency || ''
    }

    const flagSource = option.querySelector('.localization-country-option-flag')
    if (this.countryTriggerFlag && flagSource) {
      this.countryTriggerFlag.innerHTML = flagSource.innerHTML
    }
  }

  getAllowedLanguages(countryCode, option) {
    const fromOption = (option?.dataset.languages || '')
      .split(',')
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean)

    let allowed = fromOption

    if (!allowed.length && countryCode) {
      const exact = this.languageMap[countryCode]
      if (exact) {
        allowed = exact.map((code) => code.toLowerCase())
      } else {
        const upper = this.languageMap[String(countryCode).toUpperCase()]
        if (upper) {
          allowed = upper.map((code) => code.toLowerCase())
        } else {
          const lowerKey = Object.keys(this.languageMap).find(
            (key) => key.toLowerCase() === String(countryCode).toLowerCase()
          )
          if (lowerKey) {
            allowed = this.languageMap[lowerKey].map((code) => code.toLowerCase())
          }
        }
      }
    }

    // France: only offer French and English
    if (String(countryCode).toUpperCase() === 'FR') {
      const franceLanguages = ['fr', 'en']
      return (allowed.length ? allowed : franceLanguages).filter((code) =>
        franceLanguages.includes(code) || code.startsWith('fr') || code.startsWith('en')
      )
    }

    return allowed
  }

  filterLanguages(selectedOption) {
    if (!this.languageSelect || !this.allLanguageOptions.length) return

    const selected =
      selectedOption ||
      this.countryOptions.find((option) => option.getAttribute('aria-selected') === 'true')
    const countryCode = selected?.dataset.value || this.countryInput?.value || ''
    const allowed = this.getAllowedLanguages(countryCode, selected)

    const previousValue = this.languageSelect.value
    const visibleOptions = allowed.length
      ? this.allLanguageOptions.filter((option) => {
          const value = option.value.toLowerCase()
          return allowed.some((code) => value === code || value.startsWith(`${code}-`))
        })
      : [...this.allLanguageOptions]

    if (this.languageField) {
      this.languageField.hidden = false
    }

    this.languageSelect.replaceChildren()

    visibleOptions.forEach((option) => {
      const element = document.createElement('option')
      element.value = option.value
      element.textContent = option.label
      this.languageSelect.appendChild(element)
    })

    const keepPrevious = visibleOptions.some(
      (option) => option.value.toLowerCase() === previousValue.toLowerCase()
    )
    if (keepPrevious) {
      const match = visibleOptions.find(
        (option) => option.value.toLowerCase() === previousValue.toLowerCase()
      )
      this.languageSelect.value = match.value
    } else if (visibleOptions.length) {
      this.languageSelect.value = visibleOptions[0].value
    }

    if (this.languageField) {
      this.languageField.hidden = visibleOptions.length <= 1
    }
  }

  filterCountries() {
    const query = (this.searchInput?.value || '').trim().toLowerCase()
    let visibleCount = 0

    this.countryOptions.forEach((option) => {
      const match = !query || (option.dataset.search || '').includes(query)
      option.closest('li').hidden = !match
      if (match) visibleCount += 1
    })

    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0
    }

    if (this.countLabel) {
      if (query) {
        this.countLabel.hidden = false
        const template = this.dialog?.dataset.resultsCount || '[count] results'
        this.countLabel.textContent = template.replace('[count]', String(visibleCount))
      } else {
        this.countLabel.hidden = true
      }
    }
  }

  resetSearch() {
    if (this.searchInput) this.searchInput.value = ''
    this.filterCountries()
  }

  updateApplyState() {
    if (!this.applyButton) return

    const countryChanged = this.countryInput?.value !== this.initialCountry
    const languageChanged = this.languageSelect
      ? this.languageSelect.value !== this.initialLanguage
      : false

    this.applyButton.disabled = !(countryChanged || languageChanged)
  }

  scrollSelectedIntoView() {
    const selected = this.countryOptions.find((option) => option.getAttribute('aria-selected') === 'true')
    selected?.scrollIntoView({ block: 'nearest' })
  }
}

customElements.define('header-localization-switcher', HeaderLocalizationSwitcher)
